import React, {AnchorHTMLAttributes, forwardRef, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Colors, Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'

export type LinkSize = Extract<Size, 'xs' | 'sm' | 'md'>
export type LinkVariant = keyof Colors['link']

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Size variant. If no size is provided, font-size inherited from surrounding text. */
  size?: LinkSize
  /** Link variant. Can be one of 'primary', 'secondary' or 'inverse'. 'primary' by default */
  variant?: LinkVariant
  /** An Icon to display on the right side of the Link for LTR languages. Inverted on RTL */
  iconEnd?: ReactNode
}

const createVariantClass = (variantString: string) => {
  const variant = variantString as LinkVariant
  return `
    &.--${variant} {
      color: ${cssTheme.colors.link[variant].content};
    }
`
}

const LinkWrapper = styled('a')`
  .--text-xs {
    ${cssTheme.typography.bodyXS};
    font-weight: ${cssTheme.fonts.weight.md};
  }
  .--text-sm {
    ${cssTheme.typography.bodyS};
    font-weight: ${cssTheme.fonts.weight.md};
  }
  .--text-md {
    ${cssTheme.typography.bodyM};
    font-weight: ${cssTheme.fonts.weight.md};
  }

  .--no-size {
    font-size: inherit;
    line-height: inherit;
  }

  ${Object.keys(cssTheme.colors.link).map(createVariantClass).join('\n')}
`

export const TextWrapper = styled('span')`
  user-select: none;
  ${linariaMq.desktopXs} {
    user-select: auto;
  }
`

export const IconWrapper = styled.span`
  display: inline-block;
  vertical-align: text-bottom;
  &:first-child {
    margin-right: ${cssTheme.layout.spacing.s100};
  }
  &:last-child {
    margin-left: ${cssTheme.layout.spacing.s100};
  }
`

export const Link = forwardRef(
  (
    {
      children,
      size,
      variant = 'primary',
      dataId = '',
      iconEnd,
      className,
      ...anchorProps
    }: Props & AnchorHTMLAttributes<HTMLAnchorElement>,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ) => (
    <LinkWrapper
      data-id={dataId}
      className={cx(className, `--${variant}`)}
      ref={ref}
      {...anchorProps}
    >
      <TextWrapper className={cx(size ? `--text-${size}` : '--no-size')}>
        {children}
      </TextWrapper>
      {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
    </LinkWrapper>
  )
)

Link.displayName = 'Link'
